import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useLang } from "@/i18n/LanguageContext";
import { counterpartPath } from "@/i18n/routes";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, Menu as MenuIcon, X, Bell } from "lucide-react";
import { useState, useEffect } from "react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

/**
 * Top navigation used on ecommerce pages (shop, product, cart, checkout…).
 * The Home page keeps its own immersive Navbar; this one is compact.
 */
export default function ShopNav() {
  const { t, lang, setLang } = useLang();
  const { totals } = useCart();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [loc.pathname]);

  const isEs = lang === "es";
  const blogPath = isEs ? "/blog" : "/journal";
  const links = [
    { to: isEs ? "/" : "/en", label: t("nav_ecom.home") },
    { to: isEs ? "/tienda" : "/shop", label: t("nav_ecom.shop") },
    { to: blogPath, label: t("nav_ecom.journal") },
    { to: isEs ? "/contacto" : "/contact", label: t("nav_ecom.contact") },
    { to: "/faq", label: "FAQ" },
  ];

  const handleLangToggle = () => {
    const nextLang = isEs ? "en" : "es";
    const target = counterpartPath(loc.pathname, nextLang);
    setLang(nextLang);
    if (target !== loc.pathname) navigate(target);
  };

  return (
    <header
      data-testid="shop-nav"
      className="sticky top-0 z-40 bg-cream-100/95 backdrop-blur border-b border-cream-400"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`shopnav-link-${l.to.replace("/", "") || "home"}`}
              className={`text-[13px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                loc.pathname === l.to ? "text-olive-900" : "text-olive-800 hover:text-gold-deep"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleLangToggle}
            data-testid="nav-lang-toggle"
            aria-label="toggle language"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 border border-olive-700/30 hover:border-olive-700 hover:text-olive-900"
          >
            <span className={lang === "es" ? "text-olive-900 font-medium" : "text-olive-800/50"}>ES</span>
            <span aria-hidden="true" className="text-olive-800/40">/</span>
            <span className={lang === "en" ? "text-olive-900 font-medium" : "text-olive-800/50"}>EN</span>
          </button>
          {PRE_HARVEST_MODE ? (
            <a
              href="/#pedido"
              data-testid="shopnav-waitlist"
              className="relative inline-flex items-center gap-2 px-4 py-2.5 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.24em] hover:bg-olive-700 hover:text-cream-100 transition-colors"
            >
              <Bell size={15} strokeWidth={1.3} />
              <span className="hidden sm:inline">
                {lang === "es" ? "Lista de espera" : "Join waitlist"}
              </span>
            </a>
          ) : (
            <Link
              to="/carrito"
              data-testid="shopnav-cart"
              className="relative inline-flex items-center gap-2 px-4 py-2.5 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.24em] hover:bg-olive-700 hover:text-cream-100 transition-colors"
            >
              <ShoppingBag size={15} strokeWidth={1.3} />
              <span className="hidden sm:inline">{t("nav_ecom.cart")}</span>
              {totals.count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gold-deep text-cream-100 text-[10px] font-medium rounded-full"
                >
                  {totals.count}
                </span>
              )}
            </Link>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-olive-800"
            aria-label="menu"
          >
            {open ? <X size={20} strokeWidth={1.4} /> : <MenuIcon size={20} strokeWidth={1.4} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-cream-100 border-t border-cream-400">
          <nav className="flex flex-col px-6 py-5 gap-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm uppercase tracking-[0.22em] text-olive-800 py-1"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
